import {
	BooleanOptionValueDto,
	DecimalOptionValueDto,
	IntegerOptionValueDto,
	MenuFavouritesLoadInfoDto,
	OptionValueDto,
	OptionsDto,
	StaffMasterSearchDto,
	StringOptionValueDto,
	TimeStampOptionValueDto,
	Types,
} from '@siza-staff-care/shared/data';

export interface SearchAndReplace {
	search: RegExp;
	replace: string;
}

export class Settings {
	/**
	 * The user's configuration for the favourites screen.
	 */
	favourites?: MenuFavouritesLoadInfoDto;

	/**
	 * The staff search details for the current user.
	 */
	staffSearch?: StaffMasterSearchDto;

	/**
	 * The current user's StaffMaster OID
	 */
	staffMasterOid?: string;

	/**
	 * An identifier for the current user, for use by Raygun.
	 */
	identifier?: string;

	/**
	 * The current user's staffCode.
	 */
	staffCode?: string;

	/**
	 * The current user's first name.
	 */
	firstName?: string;

	/**
	 * The current user's full name.
	 */
	staffName?: string;

	sexAsGender?: boolean;

	/**
	 * The current "default-selected" StaffMaster search DTO
	 */
	stickyStaff?: StaffMasterSearchDto;

	/**
	 * The user's email address.
	 */
	email?: string;

	/**
	 * The user's login name.
	 */
	accessCode?: string;

	/**
	 * Flag to indicate that the user is allowed to access their own record, only,.
	 */
	accessOwnRecord?: boolean;

	/**
	 * The additional info displayed in the side menu - usually the system name - to distinguish systems from each other.
	 */
	additionalSystemInfo?: string;

	/**
	 * The resource bookings type name.
	 */
	allocationManagementName?: string;

	/**
	 * The resource bookings screen name
	 */
	allocationManagementScreenName?: string;

	/**
	 * Whether to allow selection of a leave episode.
	 */
	allowSelectEpisode?: boolean;

	/**
	 * Whether to allow user-defined times for shift request.
	 */
	allowUserDefinedShiftRequests?: string;

	/**
	 * The current API session's ID
	 */
	apiSessionId?: string;

	/**
	 * The number of minutes between pings to the server to keep the session alive (visible - not pending removal).
	 */
	apiSessionKeepAliveMinutes?: number;

	/**
	 * Whether bank requests are auto-authorised.
	 */
	autoAuthoriseBankRequests?: boolean;

	/**
	 * Whether the system tools menu is shown
	 */
	canShowSystemTools?: boolean;

	/**
	 * How often the dashboard auto-refreshes.
	 */
	dashboardRefreshSeconds?: number;

	/**
	 * The debug flag
	 */
	debug?: boolean;

	/**
	 * Whether to revert time value changed, by default, in the workflows.
	 */
	defaultRevertTimeValueChanges?: boolean;

	/**
	 * The default rollover date for new staff masters.
	 */
	defaultRolloverDate?: Date;

	/**
	 * Whether to default to the favourites view.
	 */
	defaultToFavouritesView?: boolean;

	/**
	 * Whether to disable clocking at login.
	 */
	disableLoginClocking?: boolean;

	/**
	 * Whether to disable polling.
	 */
	disablePoll?: boolean;

	/**
	 * Whether the available shift alerts feature is enabled.
	 */
	enableAvailableShiftAlerts?: boolean;

	/**
	 * Whether the default leave narratives feature is enabled.
	 */
	enableDefaultLeaveNarratives?: boolean;

	/**
	 * Whether the default narratives feature is enabled.
	 */
	enableDefaultNarratives?: boolean;

	/**
	 * Whether the email password reset feature is enabled.
	 */
	enableEmailPasswordReset?: boolean;

	/**
	 * Whether the expenses feature is enabled.
	 */
	enableExpenses?: boolean;

	/**
	 * Whether the Event viewer feature is enabled.
	 */
	enableEventViewer?: boolean;

	/**
	 * Whether the Skill viewer feature is enabled.
	 */
	enableSkillViewer?: boolean;

	/**
	 * Whether the external T&A feature is enabled.
	 */
	enableExternalTandA?: boolean;

	/**
	 * Whether the fill from available shift alerts feature is enabled.
	 */
	enableFillFromAvailableShiftAlerts?: boolean;

	/**
	 * Whether the floating requests feature is enabled.
	 */
	enableFloatingRequests?: boolean;

	/**
	 * Whether the folders feature is enabled.
	 */
	enableFolders?: boolean;

	/**
	 * Whether the individual mileage per claim feature is enabled.
	 */
	enableIndividualMileagePerClaim?: boolean;

	/**
	 * Whether the leave calculation feature is enabled.
	 */
	enableLeaveCalculation?: boolean;

	/**
	 * Whether the progressive rota load feature is enabled.
	 */
	enableProgressiveRotaLoad?: boolean;

	/**
	 * Whether the public API feature is enabled.
	 */
	enablePublicApi?: boolean;

	/**
	 * Whether the revert time value changes feature is enabled.
	 */
	enableRevertTimeValueChanges?: boolean;

	/**
	 * Whether the starter/leaver reasons feature is enabled.
	 */
	enableStarterLeaverReasons?: boolean;

	/**
	 * Whether TOTP MFA is enabled.
	 */
	enableTotp?: boolean;

	/**
	 * Whether to view existing from the items dashboard.
	 */
	itemsDashboardViewExisting?: boolean;

	/**
	 * Whether the items matrix feature is enabled.
	 */
	itemsMatrix?: boolean;

	/**
	 * Whether leave episodes use weekly hours
	 */
	leaveEpisodesUseWeeklyHours?: boolean;

	/**
	 * Whether the leave request limiting feature is enabled.
	 */
	limitingLeaveRequests?: boolean;

	/**
	 * The max MB file upload size.
	 */
	maxUploadFileSize?: number;

	/**
	 * The start month of the year that mileage starts. (E.g. 4 = April)
	 */
	mileagePerYearStartMonth?: number;

	/**
	 * The minimum number of characters before the staff search triggers.
	 */
	minStaffSearchLength?: number;

	myDefaultDisplayLeaveCode?: string;
	myDefaultStaffSecurityGroup?: string;
	myDefaultUnit?: string;

	networkErrorsRetryFailThreshold?: number;
	networkErrorsThreshold?: number;
	networkInitialTimeout?: number;
	networkMaxRetries?: number;
	networkNotifyPercent?: number;
	networkTimeWindowMinutes?: number;

	noStaffCareHelper?: boolean;

	/**
	 * The frequency of polling.
	 */
	pollSeconds?: number;

	/** Pending timeout multiplier */
	ptm?: number;

	/**
	 * The screen configured for routing from the alerts screen.
	 */
	requestAlertScreen?: string;

	/**
	 * Whether the resource management feature is enabled.
	 */
	roomBookings?: boolean;

	/**
	 * Whether the rota needs totalling feature is enabled.
	 */
	rotaCountsNeedsTotaling?: boolean;

	/**
	 * Whether the rota counts show unmet.
	 */
	rotaCountsShowUnmet?: boolean;

	/**
	 * Generic "search and replace" strings.
	 */
	sar?: string;

	shiftAvailabilitySaveLevelsSms?: boolean;

	/**
	 * Whether to default to folders in Staff Records
	 */
	staffMaintenanceDefaultToFolders?: boolean;

	/**
	 * The number of minutes of inactivity after which the user is logged out.
	 */
	timeoutMinutes?: number;

	/**
	 * The number of minutes of inactivity after which the user is warned about being logged out.
	 */
	timeoutWarningMinutes?: number;

	touchScrollColour?: number;
	touchScrollHeight?: number;

	/**
	 * Whether the work pattern to leave feature is enabled.
	 */
	workPatternToLeave?: boolean;

	/**
	 * Whether to suppress showing the warning when downloading a file directly instead of using Staff.Care Helper.
	 */
	dontShowStaffCareHelperWarning?: boolean;

	parsedSearchAndReplace: SearchAndReplace[] = [];

	constructor(options?: OptionsDto) {
		if (!options) {
			return;
		}

		this.setValuesFromOptions(options);
	}

	private setValuesFromOptions(options: OptionsDto) {
		const ignoreNames = [
			'oid',
			'ceased',
			'ceasedDate',
			'enableIPlanner',
			'mySoftForm',
			'myFileAttachment',
			'vClassName',
		];

		options.values.forEach((v: OptionValueDto) => {
			if (ignoreNames.indexOf(v.name) >= 0) {
				return;
			}

			switch (v.$type) {
				case Types.BooleanOptionValueDto:
					this[v.name] = (v as BooleanOptionValueDto).value;
					break;
				case Types.IntegerOptionValueDto:
					this[v.name] = (v as IntegerOptionValueDto).value;
					break;
				case Types.DecimalOptionValueDto:
					this[v.name] = (v as DecimalOptionValueDto).value;
					break;
				case Types.StringOptionValueDto:
					this[v.name] = (v as StringOptionValueDto).value;
					break;
				case Types.TimeStampOptionValueDto:
					this[v.name] = (v as TimeStampOptionValueDto).value;
					break;
				case Types.ObjectOptionValueDto:
					switch (v.name) {
						case 'staffSearch':
							this.staffSearch = v['value'];
							break;
						case 'favourites':
							this.favourites = v['value'];
							break;
						default:
							// Ignore any other object types
							break;
					}
					break;
			}
		});
	}
}
